import { useState, useCallback, type Dispatch, type SetStateAction, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, Clock } from 'lucide-react';
import { Button, EmptyState } from '@/components/v2/ui';
import { type ImageOptimizeState, type EditHistoryEntry } from './useImageOptimizeState';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
  setFile: (file: File | null) => void;
}

const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_SIZE = 20 * 1024 * 1024;

export function ImageOptimizeWarehouse({ state, setState, setFile }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'upload' | 'workspace'>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback((file: File) => {
    if (!ACCEPTED.includes(file.type)) { setError(t('imageOptimize.errors.invalidFileType')); return; }
    if (file.size > MAX_SIZE) { setError(t('imageOptimize.errors.fileTooLarge')); return; }
    setError(null);
    setFile(file);
  }, [setFile, t]);

  const onDrop = useCallback((e: DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  return (
    <div className="overflow-y-auto h-full border-r border-neutral-200 bg-neutral-50 p-4 space-y-4">
      <div className="flex gap-2">
        <Button variant={tab === 'upload' ? 'primary' : 'ghost'} size="sm" onClick={() => setTab('upload')}>
          {t('imageOptimize.uploadNew')}
        </Button>
        <Button variant={tab === 'workspace' ? 'primary' : 'ghost'} size="sm" onClick={() => setTab('workspace')}>
          {t('imageOptimize.fromWorkspace')}
        </Button>
      </div>

      {tab === 'workspace' && <EmptyState size="sm" title={t('imageOptimize.fromWorkspaceComingSoon')} />}

      {tab === 'upload' && !state.currentFile && (
        <div className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary-500 bg-primary-50' : 'border-neutral-300'}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)} onDrop={onDrop}
          onClick={() => document.getElementById('io-file-input')?.click()}>
          <Upload className="mx-auto h-8 w-8 text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-600">{t('imageOptimize.dropzone')}</p>
          <p className="text-xs text-neutral-400 mt-1">{t('imageOptimize.dropzoneHint')}</p>
          <input id="io-file-input" type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp"
            onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }} />
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {tab === 'upload' && state.currentFile && state.currentImageUrl && (
        <div className="relative inline-block">
          <img src={state.currentImageUrl} alt={state.currentFile.name} className="w-40 h-40 object-cover rounded-lg border border-neutral-200" />
          <button onClick={() => setFile(null)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            aria-label={t('common.remove')}><X className="h-3 w-3" /></button>
          <p className="text-xs text-neutral-500 mt-1 truncate max-w-[160px]">{state.currentFile.name}</p>
        </div>
      )}

      {state.editHistory.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-neutral-500 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {t('imageOptimize.history')}
          </h4>
          {state.editHistory.map((entry, i) => (
            <HistoryItem key={i} entry={entry} isActive={state.editedImageUrl === entry.imageUrl}
              onClick={() => setState(prev => ({ ...prev, editedImageUrl: entry.imageUrl, editedS3Key: entry.s3Key }))} />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryItem({ entry, isActive, onClick }: {
  entry: EditHistoryEntry; isActive: boolean; onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-2 rounded p-1.5 text-left transition-colors ${
      isActive ? 'bg-orange-50 ring-1 ring-orange-400' : 'bg-white hover:bg-neutral-100'}`}>
      <img src={entry.imageUrl} alt={t(entry.labelKey)}
        className="w-12 h-12 object-cover rounded border border-neutral-200 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-medium text-neutral-700 truncate">{t(entry.labelKey)}</p>
        <p className="text-2xs text-neutral-400">{entry.timestamp.toLocaleTimeString()}</p>
      </div>
    </button>
  );
}
